// BookingDetail.jsx
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useFetch from "../hooks/useFetch";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Navbar2 from "../components/Navbar2";
import moment from "moment/moment";

const currencyFormat = (value) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(value);
};

export function formatDate({ date, type, pickup_date, duration, tourPackage }) {
  if (!date && type !== "tour") return "-";

  if (type === "tour" && pickup_date && duration && tourPackage) {
    const pickup = moment.utc(pickup_date);
    const daysToAdd = Number(duration) - 1;
    const hoursToAdd = Number(tourPackage);

    const dropoff = pickup.clone().add(daysToAdd, "days").add(hoursToAdd, "hours");

    return dropoff.format("DD/MM/YYYY [-] HH:mm");
  }

  return moment.utc(date).format("DD/MM/YYYY [-] HH:mm");
}

const StatusBadge = ({ status, type = "booking" }) => {
  const { t } = useTranslation();

  const statusConfig = {
    booking: {
      booked: { color: "bg-yellow-100 text-yellow-800", text: t("status.booked") },
      prepared: { color: "bg-blue-100 text-blue-800", text: t("status.prepared") },
      operated: { color: "bg-indigo-100 text-indigo-800", text: t("status.operated") },
      canceled: { color: "bg-red-100 text-red-800", text: t("status.canceled") },
      success: { color: "bg-green-100 text-green-800", text: t("status.success") },
    },
    payment: {
      not_paid: { color: "bg-red-100 text-red-800", text: t("payment.not_paid") },
      paid: { color: "bg-green-100 text-green-800", text: t("payment.paid") },
      partial: { color: "bg-yellow-100 text-yellow-800", text: t("payment.partial") },
    },
  };

  const config =
    statusConfig[type]?.[status] || (type === "payment" ? statusConfig.payment.not_paid : statusConfig.booking.booked);

  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full sm:px-3 sm:py-1 sm:text-sm ${config.color}`}>
      {config.text}
    </span>
  );
};

const InfoCard = ({ icon, title, value, subtitle, className = "" }) => (
  <div className={`p-3 border rounded-lg sm:p-4 ${className}`}>
    <div className="flex items-center gap-2 mb-2">
      <i className={`fa-solid ${icon} text-slate-600`}></i>
      <p className="text-sm font-semibold text-slate-700">{title}</p>
    </div>
    <p className="text-slate-900">{value}</p>
    {subtitle && <p className="mt-1 text-xs text-slate-600 sm:text-sm">{subtitle}</p>}
  </div>
);

const AlertMessage = ({ type = "error", icon, title, message }) => {
  const alertStyles = {
    error: "border-red-200 bg-red-50 text-red-900",
    success: "border-green-200 bg-green-50 text-green-900",
    warning: "border-yellow-200 bg-yellow-50 text-yellow-900",
  };

  return (
    <div className={`flex items-start gap-3 p-4 border rounded-lg ${alertStyles[type]}`}>
      <i className={`fa-solid ${icon} mt-0.5`}></i>
      <div>
        {title && <p className="font-semibold">{title}</p>}
        <p className="text-sm">{message}</p>
      </div>
    </div>
  );
};

function BookingDetail() {
  const { t } = useTranslation();
  const { bookingCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { items: booking } = useFetch({
    module: `bookings/public/${bookingCode}`,
    enabled: !!bookingCode,
  });

  const bookingData = booking?.data;

  if (!bookingData) {
    return (
      <div className="min-h-screen px-4 py-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-md mx-auto text-center">
          <div className="p-6 bg-white border shadow-xl rounded-2xl border-slate-200 sm:p-8">
            <i className="mb-4 text-5xl text-red-500 fa-solid fa-circle-exclamation sm:text-6xl"></i>
            <h2 className="mb-4 text-xl font-bold text-slate-900 sm:text-2xl">{t("booking.not_found")}</h2>
            <p className="mb-6 text-slate-600">{t("booking.search_again")}</p>
            <button
              onClick={() => navigate("/")}
              className="w-full bg-[#f5272b] hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-all"
            >
              {t("common.back_to_search")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleContinuePayment = async () => {
    if (!bookingData?.payment_link) {
      setError(t("payment.invoice_not_found"));
      return;
    }
    setLoading(true);
    try {
      window.open(bookingData.payment_link, "_blank");
    } catch (err) {
      setError(t("payment.link_failed"));
    } finally {
      setLoading(false);
    }
  };

  const canContinuePayment =
    bookingData?.payment_status === "not_paid" &&
    bookingData?.status !== "canceled" &&
    bookingData?.status !== "completed";

  return (
    <>
      <Navbar2 darkBackground />
      <div className="min-h-screen px-4 py-6 mt-32 sm:py-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 sm:mb-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-semibold transition-all border rounded-lg sm:px-4 sm:text-base border-slate-300 hover:bg-slate-50 text-slate-700"
            >
              <i className="fa-solid fa-arrow-left"></i>
              {t("common.back")}
            </button>
            <h1 className="text-xl font-bold text-slate-900 sm:text-3xl">{t("booking.detail")}</h1>
            <div className="w-10 sm:w-20"></div>
          </div>

          {/* Main Card */}
          <div className="p-4 bg-white border shadow-xl rounded-2xl border-slate-200 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Header Info */}
              <div className="pb-4 border-b">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <h2 className="mb-2 text-lg font-bold text-slate-900 sm:text-2xl">
                      {t("booking.booking_number", { number: bookingData.number })}
                    </h2>
                    <div className="space-y-1 text-sm text-slate-600 sm:text-base">
                      <p>
                        <span className="font-semibold text-slate-700">{t("common.name")}:</span>{" "}
                        {bookingData.customer?.name}
                      </p>
                      <p>
                        <span className="font-semibold text-slate-700">{t("common.email")}:</span>{" "}
                        {bookingData.customer?.email}
                      </p>
                      <p>
                        <span className="font-semibold text-slate-700">{t("common.phone")}:</span>{" "}
                        {bookingData.customer?.phone}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusBadge status={bookingData.status} type="booking" />
                    <StatusBadge status={bookingData.payment_status} type="payment" />
                  </div>
                </div>
              </div>

              {/* Booking Type */}
              <div className="p-3 rounded-lg bg-slate-50 sm:p-4">
                <p className="mb-1 text-xs text-slate-600 sm:text-sm">{t("booking.type")}</p>
                <p className="font-semibold capitalize text-slate-900 sm:text-lg">
                  {bookingData.type.replace("_", " ")}

                  {bookingData.type === "tour" && bookingData.tour_package && (
                    <>
                      {" "}
                      - {bookingData.tour_package} {t("common.hours")}
                    </>
                  )}
                </p>
              </div>

              {/* Date Information */}
              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                <InfoCard
                  icon="fa-calendar-check"
                  title={t("booking.pickup_date")}
                  value={formatDate({
                    date: bookingData.pickup_date,
                  })}
                  subtitle={bookingData.pickup_location}
                />

                {bookingData?.type !== "transfer_service" ? (
                  <InfoCard
                    icon="fa-calendar-xmark"
                    title={t("booking.dropoff_date")}
                    value={formatDate({
                      date: bookingData.dropoff_date,
                      type: bookingData.type,
                      pickupDate: bookingData.pickup_date,
                      duration: bookingData.duration,
                      tourPackage: bookingData.tour_package,
                    })}
                    subtitle={bookingData.dropoff_location}
                  />
                ) : null}
              </div>

              {/* Price Breakdown */}
              <div className="pt-4 border-t">
                <h3 className="mb-3 text-lg font-bold text-slate-900">{t("booking.price_breakdown")}</h3>
                <div className="space-y-2">
                  {bookingData.price_calculate_data?.prices?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-600">
                        {item.type === "price" && t("booking.price_date", { date: item.date })}
                        {item.type === "fees" && t("booking.fees")}
                        {item.type === "delivery_fee" && t("booking.delivery_fee")}
                        {item.type === "pickup_fee" && t("booking.pickup_fee")}
                      </span>
                      <span className="font-semibold text-slate-900">{currencyFormat(item.price)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 text-lg font-bold border-t">
                    <span>{t("common.total")}</span>
                    <span className="text-[#f5272b]">{currencyFormat(bookingData.total_price)}</span>
                  </div>
                </div>
              </div>

              {/* Status Messages */}
              {bookingData.payment_status !== "not_paid" && (
                <AlertMessage
                  type="success"
                  icon="fa-circle-check"
                  title={t("payment.received")}
                  message={t("payment.total_paid", { amount: currencyFormat(bookingData.payment_total) })}
                />
              )}

              {bookingData.status === "canceled" && (
                <AlertMessage
                  type="error"
                  icon="fa-circle-xmark"
                  title={t("booking.canceled")}
                  message={t("booking.contact_customer_service")}
                />
              )}

              {error && <AlertMessage type="error" icon="fa-circle-exclamation" message={error} />}

              {/* Action Buttons */}
              {canContinuePayment && (
                <div className="pt-4 border-t">
                  <button
                    onClick={handleContinuePayment}
                    disabled={loading}
                    className="w-full bg-[#f5272b] hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin"></i>
                        <span>{t("common.processing")}</span>
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-credit-card"></i>
                        <span>{t("payment.continue")}</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default BookingDetail;
